import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { USER_INFO, EMAILJS_CONFIG } from "../../constants/userInfo";

// Contact form for the footer. Sends through EmailJS with the same service,
// template, and template parameters as the original ContactForm.

const EMPTY = { name: "", email: "", subject: "", message: "", website: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(f) {
  const errors = {};
  if (!f.name.trim()) errors.name = "Enter your name";
  if (!f.email.trim()) errors.email = "Enter your email";
  else if (!EMAIL_RE.test(f.email.trim())) errors.email = "Enter a valid email";
  if (!f.subject.trim()) errors.subject = "Enter a subject";
  if (!f.message.trim()) errors.message = "Enter a message";
  return errors;
}

function DevContact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "", text: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((errs) => ({ ...errs, [name]: undefined }));
    if (status.type === "error") setStatus({ type: "", text: "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    // Honeypot: real visitors never see or fill this field.
    if (form.website) {
      setStatus({ type: "success", text: "Message sent. I'll get back to you soon." });
      setForm(EMPTY);
      return;
    }

    if (!EMAILJS_CONFIG.serviceId || !EMAILJS_CONFIG.templateId || !EMAILJS_CONFIG.publicKey) {
      setStatus({ type: "error", text: `The form isn't set up yet. Email me at ${USER_INFO.email}.` });
      return;
    }

    setSending(true);
    setStatus({ type: "", text: "" });
    const name = form.name.trim();
    const email = form.email.trim();
    const subject = form.subject.trim();
    const message = form.message.trim();
    try {
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name: name,
          from_email: email,
          user_email: email,
          sender_email: email,
          to_email: USER_INFO.email,
          subject,
          message,
          reply_to: email,
          formatted_message: `Name: ${name}\nEmail: ${email}\n\nSubject: ${subject}\n\nMessage:\n${message}`,
        },
        EMAILJS_CONFIG.publicKey
      );
      setStatus({ type: "success", text: "Message sent. I'll get back to you soon." });
      setForm(EMPTY);
    } catch (err) {
      // EmailJS rejects with { status, text } describing what went wrong.
      const reason = err && err.text ? `${err.status}: ${err.text}` : String(err);
      console.error("EmailJS error:", reason, err);
      const detail = process.env.NODE_ENV === "development" ? ` (EmailJS ${reason})` : "";
      setStatus({
        type: "error",
        text: `Couldn't send your message. Try again, or email me at ${USER_INFO.email}.${detail}`,
      });
    } finally {
      setSending(false);
    }
  };

  const field = (name, label, props = {}) => {
    const Tag = props.as || "input";
    const { as, ...rest } = props;
    return (
      <label className={"cf-field" + (errors[name] ? " invalid" : "")}>
        <span className="cf-label mono">{label}</span>
        <Tag
          name={name}
          value={form[name]}
          onChange={onChange}
          aria-invalid={Boolean(errors[name])}
          disabled={sending}
          {...rest}
        />
        {errors[name] && <span className="cf-error">{errors[name]}</span>}
      </label>
    );
  };

  return (
    <form className="dcard cf" onSubmit={onSubmit} noValidate>
      <div className="cf-head mono">
        <span className="cf-dot" />
        <span className="cf-dot" />
        <span className="cf-dot" />
        <span className="muted">new-message.md</span>
      </div>
      <div className="cf-row">
        {field("name", "name", { type: "text", placeholder: "Jane Doe", autoComplete: "name" })}
        {field("email", "email", {
          type: "email",
          placeholder: "jane@company.com",
          autoComplete: "email",
        })}
      </div>
      {field("subject", "subject", { type: "text", placeholder: "Senior engineer role at Acme" })}
      {field("message", "message", {
        as: "textarea",
        rows: 5,
        placeholder: "A few lines about the role or project",
      })}
      <input
        className="cf-honeypot"
        type="text"
        name="website"
        value={form.website}
        onChange={onChange}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <div className="cf-actions">
        <button type="submit" className="btn-primary cf-submit" disabled={sending}>
          {sending ? "Sending…" : "Send message"}
        </button>
        {status.text && (
          <span className={"cf-status " + status.type} role="status">
            {status.text}
          </span>
        )}
      </div>
    </form>
  );
}

export default DevContact;
